/**
 * GalleryLightbox — Voll-Viewer fuer die Bilder-Galerie.
 *
 * Spiegelt das Keyboard-/Focus-Trap-Pattern des Guest-PhotoLightbox, laedt das
 * Bild aber ueber AuthenticatedImage (Admin-Bearer-Auth statt Guest-Token) in
 * der `original`-Variante. Rein lokaler Viewer.
 */
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { useCallback, useEffect, useRef } from 'react';
import type { PropertyFileResponse } from '@/shared/api';
import { AuthenticatedImage } from './AuthenticatedImage';

interface GalleryLightboxProps {
  propertyId: string;
  images: PropertyFileResponse[];
  index: number;
  onClose: () => void;
  onNavigate: (nextIndex: number) => void;
}

export function GalleryLightbox({
  propertyId,
  images,
  index,
  onClose,
  onNavigate,
}: GalleryLightboxProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const total = images.length;

  const go = useCallback(
    (delta: number) => onNavigate((index + delta + total) % total),
    [index, total, onNavigate],
  );

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowRight') go(1);
      else if (e.key === 'ArrowLeft') go(-1);
      else if (e.key === 'Tab') {
        const focusables = dialogRef.current?.querySelectorAll<HTMLElement>('button');
        if (!focusables || focusables.length === 0) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (!first || !last) return;
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [go, onClose]);

  // Initial-Fokus nur einmal beim Öffnen — sonst würde jeder Parent-Rerender
  // (Refetch, dragging-Toggle) den Fokus zurück auf den Schließen-Button reißen.
  useEffect(() => {
    dialogRef.current?.querySelector<HTMLElement>('button')?.focus();
  }, []);

  const image = images[index];
  if (!image) return null;

  const arrowClass =
    'absolute flex size-11 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20';

  return (
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-label={image.fileName}
      className="z-100 bg-ink/90 fixed inset-0 flex items-center justify-center p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <button
        type="button"
        aria-label="Schließen"
        onClick={onClose}
        className="absolute right-4 top-4 flex size-10 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20"
      >
        <X className="size-5" aria-hidden />
      </button>
      {total > 1 && (
        <button
          type="button"
          aria-label="Zurück"
          onClick={() => go(-1)}
          className={`${arrowClass} left-4`}
        >
          <ChevronLeft className="size-6" aria-hidden />
        </button>
      )}
      <AuthenticatedImage
        key={image.id}
        propertyId={propertyId}
        fileId={image.id}
        variant="original"
        alt={image.fileName}
        className="max-h-[85vh] max-w-full rounded-lg bg-transparent object-contain"
      />
      {total > 1 && (
        <button
          type="button"
          aria-label="Weiter"
          onClick={() => go(1)}
          className={`${arrowClass} right-4`}
        >
          <ChevronRight className="size-6" aria-hidden />
        </button>
      )}
      <span className="absolute bottom-4 flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-white">
        <span className="max-w-[40vw] truncate font-mono text-xs">{image.fileName}</span>
        <span className="font-mono text-xs opacity-70">
          {index + 1} / {total}
        </span>
      </span>
    </div>
  );
}
